import { sendLaunchDarklyMetric } from './launchdarkly-track';

/**
 * Tracks clicks from a property tile through to the property page for the
 * updated-property-tile experiment.
 *
 * Delegated at document level, in the capture phase, so that server-rendered
 * Blade tiles and the Vue tiles that later replace them in the same container
 * are both covered by one code path. Per-component handlers would miss every
 * click landing before hydration, and would not fire at all for clicks on the
 * anchor's children.
 */
(function () {
  const TILE_SELECTOR = 'a[data-cy="property-listing"]';
  const IGNORE_SELECTOR =
    '[data-tile-click-ignore], .swiper-pagination, button, [role="button"], label, input, select, textarea';
  const GRID_SELECTOR =
    '#properties_listing_container, #properties_nearby_listing_container';
  const EVENT_NAME = 'property_tile_clicked';
  const SHARE_BUTTON_SELECTOR = '[data-property-share]';

  function wrapperFor(tile) {
    return tile.closest('[data-id]');
  }

  function positionFor(wrapper) {
    if (!wrapper || !wrapper.parentElement) {
      return null;
    }

    const siblings = Array.from(wrapper.parentElement.children).filter((el) =>
      el.hasAttribute('data-id')
    );
    const index = siblings.indexOf(wrapper);

    return index === -1 ? null : index + 1;
  }

  function gridFor(tile) {
    const grid = tile.closest(GRID_SELECTOR);

    return grid ? grid.id : null;
  }

  function trackTileClick(tile) {
    const wrapper = wrapperFor(tile);

    sendLaunchDarklyMetric(EVENT_NAME, {
      property_id: wrapper ? wrapper.dataset.id : null,
      position: positionFor(wrapper),
      grid: gridFor(tile),
    });
  }

  async function shareProperty(button) {
    const shareData = {
      title: button.dataset.shareTitle || 'Check out this property',
      url: button.dataset.shareUrl,
    };

    if (!shareData.url) {
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
      }
    }

    let copied = false;

    try {
      await navigator.clipboard.writeText(shareData.url);
      copied = true;
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = shareData.url;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);

      try {
        input.select();
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      } finally {
        input.remove();
      }
    }

    const status = button.parentElement.querySelector(
      '[data-property-share-status]'
    );

    if (!status) {
      return;
    }

    status.textContent = copied ? 'Link copied' : 'Unable to copy link';
    window.setTimeout(() => {
      status.textContent = '';
    }, 2000);
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const shareButton = event.target.closest(SHARE_BUTTON_SELECTOR);

    if (!shareButton) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    shareProperty(shareButton);
  });

  document.addEventListener(
    'click',
    function (event) {
      if (
        !window.config ||
        !window.config.launchDarkly ||
        !window.config.launchDarkly.enabled
      ) {
        return;
      }

      if (!(event.target instanceof Element)) {
        return;
      }

      // Controls that sit inside the tile anchor but never navigate.
      if (event.target.closest(IGNORE_SELECTOR)) {
        return;
      }

      const tile = event.target.closest(TILE_SELECTOR);

      if (!tile) {
        return;
      }

      trackTileClick(tile);
    },
    true
  );
})();
